import React, { useRef } from 'react';
import type { SchedulingMethod } from '@/features/products/types/products.types';

interface SchedulingMethodSelectorProps {
    methods: SchedulingMethod[];
    selectedKey: string | null;
    onChange: (method: SchedulingMethod) => void;
    /** Group-level inert state. UNCHANGED — keeps using the NATIVE `disabled`
     *  attribute. "This whole control is inert; there is nothing to explain." */
    disabled?: boolean;
    /**
     * Per-method unavailability, keyed by `method.key` (#750). A method with
     * an entry is rendered `aria-disabled` (NOT natively `disabled`) and its
     * reason is rendered visibly beneath the group and bound via
     * `aria-describedby`. "This one option cannot be chosen, and here is
     * why."
     */
    unavailableReasons?: Readonly<Record<string, string>>;
}

/**
 * Accessible radiogroup for choosing how a quote's total is spread over
 * time (`Product.schedulingMethods`, #474). Implemented as native
 * `<button role="radio">` elements with a roving tabindex per the ARIA APG
 * radiogroup pattern — native buttons already activate on Enter/Space, so
 * only arrow-key roving focus/selection needs wiring by hand. Not a raw
 * `<input type="radio">` group, and not the shared `Select` (a dropdown,
 * not a same-screen segmented choice).
 *
 * Two DIFFERENT mechanisms mark an option "cannot be chosen right now", and
 * they are deliberately NOT the same one (#750):
 *
 * - **Group-level `disabled` (unchanged, native):** the whole control is
 *   inert — there is nothing to explain, so native buttons being excluded
 *   from the tab order when `disabled` is exactly the right behaviour.
 * - **Per-method `unavailableReasons` (new, `aria-disabled`):** one option
 *   cannot be chosen, and there is a SPECIFIC reason that must be reachable.
 *   A natively `disabled` button is removed from the tab order, cannot be
 *   focused, and is skipped or under-announced by assistive technology — so
 *   its `aria-describedby` reason would be unreachable to precisely the
 *   users who most need it stated. The APG pattern for a disabled control
 *   that must still explain itself is `aria-disabled="true"` on a focusable
 *   element whose activation handler no-ops, which is what this component
 *   does for a per-method reason. Do NOT "fix" this by making per-method
 *   unavailability native `disabled` too — that was tried in spirit by the
 *   previous version of this docblock and is exactly the mistake #750
 *   corrects.
 */
export const SchedulingMethodSelector: React.FC<SchedulingMethodSelectorProps> = ({
    methods,
    selectedKey,
    onChange,
    disabled = false,
    unavailableReasons,
}) => {
    const buttonRefs = useRef<Array<HTMLButtonElement | null>>([]);

    /** The reason a method is unavailable, or `undefined` when it is
     *  available. Type-guarded rather than read bare: `unavailableReasons`
     *  is a plain object, so a key colliding with an Object.prototype member
     *  (`constructor`, `toString`) would otherwise read back a function and
     *  render as "[object Function]". Cheap to exclude; impossible to spot
     *  in review later. */
    const reasonFor = (key: string): string | undefined => {
        const reason = unavailableReasons?.[key];
        return typeof reason === 'string' ? reason : undefined;
    };

    if (methods.length === 0) {
        return (
            <div className="p-4 bg-mp-background border border-mp-border rounded-lg text-sm text-mp-muted">
                No scheduling methods are configured for this product.
            </div>
        );
    }

    // Roving tabindex (ARIA APG). Selection ALWAYS wins when there is one —
    // the selection is never silently moved, and a selected-then-unavailable
    // option must stay reachable so its reason can be read. With nothing
    // selected, prefer the first AVAILABLE option; fall back to 0 when EVERY
    // option is unavailable, so the group is never removed from the tab
    // order entirely.
    const selectedIndex = methods.findIndex((method) => method.key === selectedKey);
    const firstAvailableIndex = methods.findIndex((method) => reasonFor(method.key) === undefined);
    const tabbableIndex =
        selectedIndex !== -1 ? selectedIndex : firstAvailableIndex !== -1 ? firstAvailableIndex : 0;

    const focusIndex = (index: number) => {
        buttonRefs.current[index]?.focus();
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
        if (disabled) return;

        let nextIndex: number | null = null;
        switch (event.key) {
            case 'ArrowRight':
            case 'ArrowDown':
                nextIndex = (index + 1) % methods.length;
                break;
            case 'ArrowLeft':
            case 'ArrowUp':
                nextIndex = (index - 1 + methods.length) % methods.length;
                break;
            case 'Home':
                nextIndex = 0;
                break;
            case 'End':
                nextIndex = methods.length - 1;
                break;
            default:
                return;
        }

        event.preventDefault();
        // FOCUS ALWAYS MOVES — there is no path on which an unavailable
        // destination silently swallows the key press. Selection follows
        // focus only onto an AVAILABLE option (#750): the APG pattern for a
        // radio group containing a disabled option is exactly this — focus
        // is unconditional, selection is conditional. The order (focus
        // first, selection second) is a behaviour-neutral reordering for an
        // AVAILABLE destination — React batches the onChange state update
        // until after this handler returns, and the buttons are keyed by
        // `method.key`, so no node is remounted by the re-render and the
        // element that ends up focused is identical either way. It is
        // inverted only so that focus still moves on the path where
        // onChange is skipped.
        focusIndex(nextIndex);
        if (reasonFor(methods[nextIndex].key) === undefined) {
            onChange(methods[nextIndex]);
        }
    };

    return (
        <div className="space-y-2">
            <div role="radiogroup" aria-label="Scheduling method" className="flex flex-wrap gap-2">
                {methods.map((method, index) => {
                    const reason = reasonFor(method.key);
                    const isSelected = method.key === selectedKey;
                    return (
                        <button
                            key={method.key}
                            ref={(el) => {
                                buttonRefs.current[index] = el;
                            }}
                            type="button"
                            role="radio"
                            aria-checked={isSelected}
                            // React renders `aria-disabled="false"` for a
                            // literal `false`, which is a MEANINGFULLY
                            // DIFFERENT announcement from the attribute being
                            // absent. Available options must be
                            // byte-for-byte as before #750 — no
                            // `aria-disabled` attribute at all.
                            aria-disabled={reason !== undefined || undefined}
                            aria-describedby={
                                reason !== undefined
                                    ? `scheduling-method-reason-${method.key}`
                                    : undefined
                            }
                            tabIndex={index === tabbableIndex ? 0 : -1}
                            disabled={disabled}
                            onClick={() => {
                                if (reason === undefined) onChange(method);
                            }}
                            onKeyDown={(event) => handleKeyDown(event, index)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium border transition ${
                                isSelected
                                    ? 'bg-mp-primary text-mp-primary-foreground border-mp-primary'
                                    : 'bg-mp-surface text-mp-foreground border-mp-input hover:bg-mp-background'
                            } ${disabled || reason !== undefined ? 'cursor-not-allowed opacity-50' : ''}`}
                        >
                            {method.label}
                        </button>
                    );
                })}
            </div>

            {/* One line per unavailable method, OUTSIDE the radiogroup so the
                group owns only radios. Rendered visibly so the sighted
                salesperson and the AT user get THE SAME information —
                `aria-describedby` points at visible text, never at a `title`
                (not reliably announced, not keyboard-reachable) or folded
                into `aria-label` (which would override the visible label). */}
            {methods.map((method) => {
                const reason = reasonFor(method.key);
                return reason === undefined ? null : (
                    <p
                        key={method.key}
                        id={`scheduling-method-reason-${method.key}`}
                        data-testid="scheduling-method-reason"
                        className="text-sm text-mp-muted"
                    >
                        {reason}
                    </p>
                );
            })}
        </div>
    );
};
